/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import styles from './styles';
import { Box, Divider, Grid, IconButton, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { CancelPresentationRounded, ContentCopyOutlined, DeleteRounded, SaveRounded } from '@mui/icons-material';
import columns from './table_config';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useDispatch, useSelector } from 'react-redux';
import useUnsavedChanges from '../../../../../hooks/useUnsavedChanges';
import SelectEditInputTextField from '../../../../ui/SelectEditInputTextField';
import SelectEditValueTypes from '../../../../ui/SelectEditValueTypes';
import Datagrid from '../../../../ui/Datagrid';
import DeleteConfirmationModal from '../../../../deleteConfirmationModal';
import { enqueueSnackbar } from '../../../../../store/actions/snackbar';
import ModalDialog from '../../../../ui/ModalDialog';
import { isMobile } from 'react-device-detect';
import MobileMudpumpRunInput from '../../../../mobile/hydraulics/featuredWellRunInputs/rolloverRunInputs/mudpumpsRunInput';
import MobileSelectEditInputTextField from '../../../../mobile/hydraulics/SelectEditInputTextField';
import MobileSelectEditValueTypes from '../../../../mobile/hydraulics/SelectEditValueTypes';

function generateRandom() {
  const length = 24;
  const charset = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let retVal = '';
  for (let i = 0, n = charset.length; i < length; ++i) {
    retVal += charset.charAt(Math.floor(Math.random() * n));
  }
  return retVal;
}

const MudPumpRunInputsModel = ({
  onCloseClick,
  mudPumpData,
  setMudPumpData,
  isOpen,
  setParameterDirty,
}) => {
  const classes = styles();
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const hydraulicsLanding = useSelector(state => state.hydraulicsLanding);
  const [properties, setProperties] = useState([]);
  const [invalidRows, setInvalidRows] = useState({});
  const [deleteAction, setDeleteAction] = useState(false);
  const [deleteRow, setDeleteRow] = useState();
  const {
    unSavedExists,
    handleClose,
    UnsavedChangesModal
  } = useUnsavedChanges(properties, onCloseClick);

  useEffect(() => {
    if (!hydraulicsLanding.loading) {
      const details = mudPumpData.map((details) => {
        return {
          ...details,
        };
      });
      setProperties(details);
    }
    return () => {
      setProperties([]);
    };
  }, [hydraulicsLanding?.data?.mudPumps]);

  const handleAddProperty = (row = null) => {
    const currentProperties = [...properties];
    const nextId = currentProperties.length - 1;
    const newRow = {
      pumpType: row?.pumpType ?? 'Triplex',
      liner_size: row?.liner_size ?? '',
      stroke_length: row?.stroke_length ?? '',
      efficiency: row?.efficiency ?? '',
      bbl_stk: row?.bbl_stk ?? 0.0,
      grid_id: nextId + 1,
      _id: generateRandom(),
      isNewRow: true,
      isDirty: true,
    };
    currentProperties.unshift(newRow);
    setProperties(currentProperties);
  };

  const calculateBBLStk = (row) => {
    const { liner_size = 0, stroke_length = 0, efficiency = 100, pumpType } = row;
    let bbl_stk;

    bbl_stk = (0.000243 * Math.pow(liner_size, 2) * stroke_length) * (efficiency / 100);

    if (pumpType === 'Quad') {
      bbl_stk *= 4 / 3;
    }

    return bbl_stk.toFixed(6);
  };

  const updateData = (id, field, value) => {
    const numericValue = Number(value);
    let errorMessage = '';

    const min_linerSize = hydraulicsLanding?.units?.mudPumps?.liner_size.min;
    const max_linerSize = hydraulicsLanding?.units?.mudPumps?.liner_size.max;
    const min_stroke_length = hydraulicsLanding?.units?.mudPumps?.stroke_length.min;
    const max_stroke_length = hydraulicsLanding?.units?.mudPumps?.stroke_length.max;
    const min_efficiency = hydraulicsLanding?.units?.mudPumps?.efficiency.min;
    const max_efficiency = hydraulicsLanding?.units?.mudPumps?.efficiency.max;

    if (
      field === 'liner_size' &&
      (isNaN(numericValue) || numericValue < min_linerSize || numericValue > max_linerSize)
    ) {
      errorMessage = `Liner size should be a number between ${min_linerSize} and ${max_linerSize}`;
    } else if (
      field === 'stroke_length' &&
      (isNaN(numericValue) || numericValue < min_stroke_length || numericValue > max_stroke_length)
    ) {
      errorMessage = `Stroke length should be a number between ${min_stroke_length} and ${max_stroke_length}`;
    } else if (
      field === 'efficiency' &&
      (isNaN(numericValue) || numericValue < min_efficiency || numericValue > max_efficiency)
    ) {
      errorMessage = `Efficiency should be a number between ${min_efficiency} and ${max_efficiency}`;
    }

    if (errorMessage) {
      setInvalidRows(prev => ({ ...prev, [id]: true }));
      dispatch(
        enqueueSnackbar(
          errorMessage,
          'error',
          new Date().getTime() + Math.random()
        )
      );
    } else {
      setInvalidRows(prev => ({ ...prev, [id]: false }));
      setProperties(prevProperties => {
        const updatedProperties = prevProperties.map(item => {
          if (item._id === id) {
            const isDirty = value !== item[field];

            // Ensure numeric fields are stored as numbers
            const shouldBeNumber = ['liner_size', 'stroke_length', 'efficiency'];
            const parsedValue = shouldBeNumber.includes(field) ? numericValue : value;

            const updatedItem = { ...item, [field]: parsedValue, isDirty };

            if (
              field === 'liner_size' ||
              field === 'stroke_length' ||
              field === 'efficiency' ||
              field === 'pumpType'
            ) {
              updatedItem.bbl_stk = Number(calculateBBLStk(updatedItem));
            }

            return updatedItem;
          }
          return item;
        });
        return updatedProperties;
      });
    }
  };

  const checkMudPumpValues = (row) => {
    const invalidFields = [];
    if (!row.bbl_stk || Number(row.bbl_stk) <= 0) {
      if (!row.liner_size && !row.stroke_length && !row.efficiency) {
        setInvalidRows(prev => ({ ...prev, [row._id]: true }));
        dispatch(
          enqueueSnackbar(
            'Output (bbl/stk) must be greater than zero.',
            'error',
            new Date().getTime() + Math.random()
          )
        );
        return false;
      }
    }
    if (invalidFields.length === 0) {
      setInvalidRows(prev => ({ ...prev, [row._id]: false }));
      return true;
    } else {
      setInvalidRows(prev => ({ ...prev, [row._id]: true }));
      dispatch(
        enqueueSnackbar(
          `Please enter the following details: ${invalidFields.join(', ')}`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };

  const saveProperty = (row) => {
    if (row.bbl_stk <= 0) {
      dispatch(
        enqueueSnackbar(
          'Pump output should be greater than zero.',
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return;
    }

    if (checkMudPumpValues(row)) {
      setProperties((prev) => {
        const updated = prev.map((item) =>
          item._id === row._id
            ? { ...item, isNewRow: false, isDirty: false }
            : item
        );

        // ✅ defer parent updates
        queueMicrotask(() => {
          setMudPumpData(updated);
          setParameterDirty((prev) => {
            if (!Array.isArray(prev) || prev.length === 0) return prev;
            const updatedDirty = [...prev];
            updatedDirty[0] = { ...updatedDirty[0], isDirty: true };
            return updatedDirty;
          });
        });

        return updated;
      });
    }
  };

  const handleDelete = (row) => {
    setProperties((prev) => {
      const updated = prev.filter((item) => item._id !== row._id);
      setMudPumpData(updated);
      return updated;
    });
    setDeleteAction(false);
    setDeleteRow({});
    setParameterDirty(prev => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const updated = [...prev];
      updated[0] = { ...updated[0], isDirty: true };
      return updated;
    });
  };

  const renderValueCell = (params) => {
    if (isMobile) {
      return (
        <MobileSelectEditInputTextField numberFormatOnly {...params} updateData={updateData} />
      );
    }
    return (
      <SelectEditInputTextField
        numberFormatOnly
        {...params}
        updateData={updateData}
      />
    );
  };

  const renderActionCell = ({ row }) => {
    const isRowInvalid = invalidRows[row._id];
    return (
      <Grid container justifyContent="center">
        <IconButton
          variant="solid"
          disabled={!row.isDirty || isRowInvalid}
          onClick={() => {
            saveProperty(row);
          }}
        >
          <SaveRounded />
        </IconButton>
        <IconButton
          variant="solid"
          disabled={row?.isNewRow}
          onClick={() => {
            handleAddProperty(row);
          }}
        >
          <ContentCopyOutlined />
        </IconButton>
        <IconButton
          variant="solid"
          onClick={() => {
            setDeleteAction(true);
            setDeleteRow(row);
          }}
        >
          <DeleteRounded />
        </IconButton>
      </Grid>
    );
  };

  const renderTypeCell = (params) => {
    if (isMobile) {
      return (
        <MobileSelectEditValueTypes
          {...params}
          closeCellAfterSelect={true}
          updateData={updateData}
          data={['Triplex', 'Quad']}
        />
      );
    }
    return (
      <SelectEditValueTypes
        {...params}
        closeCellAfterSelect={true}
        updateData={updateData}
        data={['Triplex', 'Quad']}
      />
    );
  };

  const closeHandler = () => {
    if (unSavedExists) {
      handleClose(false);
      return;
    }
    onCloseClick();
  };

  const isCellEditable = (params) => {
    if (params.field === 'bbl_stk') {
      const { liner_size, stroke_length, efficiency } = params.row;
      return !((liner_size > 0) || (stroke_length > 0) || (efficiency > 0));
    }
    return true;
  };

  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onCloseClick}
      dialogClass={classes.dialogModal}
      fullWidth={true}
    >
      <Box className={classes.dialogTitle}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>  {'Mud Pumps'}</Typography>
          </Grid>
          <Grid item>
            <Box display="flex" alignItems="center">
              <IconButton
                onClick={closeHandler}
              >
                <CancelPresentationRounded fontSize='large' />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Divider />
      <Grid container>
        <Grid item className={classes.customTool} xs={2} justifyContent="space-between" >
          <Box>
            <Typography
              variant='button'
              fontWeight='700'
              className={hydraulicsLanding.loading || properties[0]?.isNewRow ? classes.disableIcon : ''}
              onClick={handleAddProperty}>
              + Add
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12}>
          {isMobile
            ? (
              <Grid item xs={12} padding={'4px'}>
                <MobileMudpumpRunInput
                  data={properties}
                  columns={columns(renderValueCell, renderActionCell, renderTypeCell, hydraulicsLanding?.units?.mudPumps)}
                  updateData={updateData}
                  loading={hydraulicsLanding.loading}
                />
              </Grid>
              )
            : (
              <Datagrid
                apiRef={apiRef}
                data={properties}
                loading={hydraulicsLanding.loading}
                getRowId={(row) => row._id}
                columns={columns(renderValueCell, renderActionCell, renderTypeCell, hydraulicsLanding?.units?.mudPumps)}
                disableStickyHeader={false}
                autoHeight={false}
                height={'25vh'}
                sx={{
                  '.MuiDataGrid-cell': {
                    fontSize: '1rem',
                    fontWeight: 900
                  },
                  '.MuiDataGrid-columnHeader': {
                    fontSize: '1.2rem',
                    fontWeight: '900 !important'
                  }
                }}
                getRowClassName={(params) => {
                  return invalidRows[params.id] ? classes.invalidRow : '';
                }}
                pageSize={100}
                isCellEditable={isCellEditable}
                editMode="cell"
                toolbar={{
                  options: {
                    columns: false,
                    filters: false,
                    density: false,
                    export: false
                  },
                }}
                enableDirtyLock
              />
              )}
        </Grid>
      </Grid>
      {
        deleteAction && <DeleteConfirmationModal
          open={deleteAction}
          handleDelete={handleDelete}
          handleClose={setDeleteAction}
          deleteRow={deleteRow}
        />
      }
      <UnsavedChangesModal />
    </ModalDialog>
  );
};
MudPumpRunInputsModel.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  mudPumpData: PropTypes.any,
  setMudPumpData: PropTypes.func,
  isOpen: PropTypes.bool.isRequired,
  setParameterDirty: PropTypes.func
};

export default MudPumpRunInputsModel;
